import { Logger, ArrayUtils } from "neo-common";
import {
    ICheckOptions,
    IReelTeasingAttributes,
    ISymbolTeasingAttributes,
    ReelTeasingActions,
    SymbolTeasingActions,
    TeasingComponentAbstract,
} from "./TeasingComponentAbstract";
import {
    Configuration,
    GeneralConfigurationAttributes,
    MachineConfigurationAttributes,
} from "../Components/Configuration";
import { Settings } from "./Settings";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { Tweens } from "../Components/Effects/Tweens";

/**
 * @author Marek Turkovic
 * @date 01.10.2019
 */
export class TeasingBonus extends TeasingComponentAbstract {
    //static Teasing: string = 'teasing';
    //static TeasingReel: string = 'teasingReel';
    //static TeasingFail: string = 'teasingFail';
    //static TeasingWin: string = 'teasingWIn';

    eventTeasing: string = "teasing";
    eventTeasingReel: string = "teasingReel";
    eventTeasingFail: string = "teasingFail";
    eventTeasingWin: string = "teasingWIn";

    private spinBitmask: number = 0;

    public eventEmitter: FrameTimerEventEmitter;

    constructor() {
        super();

        // CONFIG PROPERTIES
        this.reelCount = 5;
        this.teasingStopDelay = 90;
        this.minimumSymbolsForTeasing = 2;

        // OTHER PROPERTIES
        this.countSymbolsOnReelsAndRows = 0;
        this.canPlayTeaser = false;
        this.wasPlayedTeaser = false;
        this.wasPlayedTeaserWin = false;
        this.wasPlayedTeaserFail = false;
        this.teasingSymbol = Settings.SymbolBonus;
        this.teasingSymbols = [
            Settings.SymbolBonus,
            Settings.SymbolBonus + 1,
            Settings.SymbolBonus + 2,
            Settings.SymbolBonus + 3,
            Settings.SymbolBonus + 4,
        ];

        // TIMERS
        this.eventEmitter = CreateFrameTimer("TeasingBonus");

        this.finishTeasing = this.finishTeasing.bind(this);

        // CONFIGS
        let configs: Array<string> = ["GeneralConfiguration", "MachineConfiguration"];
        for (let i = 0; i < configs.length; i++) {
            let config = Configuration.Configurations[configs[i]];
            if (config != null) {
                this.configurate(config);
            }
        }

        // REELS DEFAULT
        for (let i = 0; i < this.reelCount; i++) {
            this.reelsTeasingAttributes[i] = {
                action: ReelTeasingActions.DO_NOTHING,
                stopping: false,
                stopDelay: i * this.stopDelay,
            };
        }

        // SYMBOLS DEFAULT
        for (let i = 0; i < this.reelCount; i++) {
            if (this.symbolsTeasingAttributes[i] == null) {
                this.symbolsTeasingAttributes[i] = [];
            }
            for (let j = 0; j < this.visibleSymbolCount; j++) {
                this.symbolsTeasingAttributes[i][j] = { teasable: false, action: SymbolTeasingActions.NOWIN };
            }
        }

        this._customData = [];
    }

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    protected configurate(node: Element): void {
        if (node.hasAttribute(GeneralConfigurationAttributes.REEL_COUNT)) {
            this.reelCount = parseInt(node.getAttribute(GeneralConfigurationAttributes.REEL_COUNT));
        }

        if (node.hasAttribute(GeneralConfigurationAttributes.VISIBLE_SYMBOL_COUNT)) {
            this.visibleSymbolCount = parseInt(node.getAttribute(GeneralConfigurationAttributes.VISIBLE_SYMBOL_COUNT));
        }

        /*if (node.hasAttribute(MachineConfigurationAttributes.TEASING_SYMBOLS)) {
            this._teasingSymbols = ArrayUtils.numberArray(node.getAttribute(MachineConfigurationAttributes.TEASING_SYMBOLS));
        }*/

        if (node.hasAttribute(MachineConfigurationAttributes.TEASING_STOP_DELAY)) {
            this.teasingStopDelay = parseInt(node.getAttribute(MachineConfigurationAttributes.TEASING_STOP_DELAY));
        }

        if (node.hasAttribute(GeneralConfigurationAttributes.REEL_X)) {
            this.reelX = ArrayUtils.numberArray(node.getAttribute(GeneralConfigurationAttributes.REEL_X));
        }

        if (node.hasAttribute(GeneralConfigurationAttributes.REEL_Y)) {
            this.reelY = ArrayUtils.numberArray(node.getAttribute(GeneralConfigurationAttributes.REEL_Y));
        }

        if (node.hasAttribute(MachineConfigurationAttributes.STOP_DELAY)) {
            this.stopDelay = parseInt(node.getAttribute(MachineConfigurationAttributes.STOP_DELAY));
        }
    }

    public addElementInstance(element: Object): void {
        this.elementInstance[Object.keys(element)[0]] = element[Object.keys(element)[0]];
    }

    //******************************************************************************************************************
    //  CHECK REELS
    //******************************************************************************************************************

    /**
     * Check called before reel.spinStop(). Checks if teasing is possible and checks which reel can be stopped immediately.
     * @param {ICheckOptions} options
     */
    public checkOnInit(options: ICheckOptions): Array<IReelTeasingAttributes> {
        this.stoppedSymbolsArray = options.stoppedSymbols;
        this.force = options.force;
        if (options["spinBitmask"] != null) {
            this.spinBitmask = options["spinBitmask"];
        }
        let aditionalDelay: number;

        if (options.force) {
            this.teasingEnabled = false;
        } else {
            this.teasingEnabled = this.isEnoughtForStartTeasing(0, this.reelCount - 1);
        }

        if (!this.teasingEnabled) {
            aditionalDelay = 0;
        } else {
            aditionalDelay = 0;
        }

        this.setReelsTeasingAttributes(aditionalDelay);

        return this.reelsTeasingAttributes;
    }

    /**
     * Set teasing attributes (delay, action,..) for every reel.
     * @param {number} aditionalDelay
     */
    protected setReelsTeasingAttributes(aditionalDelay: number): void {
        let countOfSymbol = 0;

        // first - get count of teasing symbols on held reels
        for (let reelIndex = 0; reelIndex < this.reelCount; reelIndex++) {
            if (this.isReelHeld(this.spinBitmask, reelIndex)) {
                countOfSymbol += this.getCountOfSymbolsOnSelectedReelsAndRows(
                    reelIndex,
                    reelIndex,
                    Settings.SymbolBonus,
                );
                //this.reelsTeasingAttributes[reelIndex].action = ReelTeasingActions.DO_NOTHING;
            }
        }
        // second - set non-held reels attributes
        for (let reelIndex = 0; reelIndex < this.reelCount; reelIndex++) {
            if (!this.isReelHeld(this.spinBitmask, reelIndex)) {
                if (countOfSymbol >= this.minimumSymbolsForTeasing) {
                    aditionalDelay += this.teasingStopDelay;
                    this.reelsTeasingAttributes[reelIndex].action = ReelTeasingActions.TEASE;
                } else {
                    this.reelsTeasingAttributes[reelIndex].action = ReelTeasingActions.SPIN_STOP;
                }
                this.reelsTeasingAttributes[reelIndex].stopDelay = reelIndex * this.stopDelay + aditionalDelay;

                countOfSymbol += this.getCountOfSymbolsOnSelectedReelsAndRows(
                    reelIndex,
                    reelIndex,
                    Settings.SymbolBonus,
                );
            }
        }
    }

    /**
     *
     * @param {number} reelIndexStart
     * @param {number} reelIndexStop
     * @returns {boolean}
     */
    protected isEnoughtForStartTeasing(reelIndexStart = 0, reelIndexStop: number): boolean {
        return this.countSymbols(reelIndexStart, reelIndexStop, this.teasingSymbol) >= this.minimumSymbolsForTeasing;
    }

    /**
     * Count symbols of some kind on reels.
     * @param {number} reelIndexStart
     * @param {number} reelIndexStop
     * @param symbolId
     * @returns {number}
     *
     * todo Rename this method to countStoppedSymbols?
     */
    public countSymbols(reelIndexStart = 0, reelIndexStop: number, symbolId: number): number {
        let count = 0;

        for (let i = reelIndexStart; i <= reelIndexStop; i++) {
            if (this.stoppedSymbolsArray[i].symbols.indexOf(symbolId) !== -1) {
                count++;
            }
        }

        return count;
    }

    /**
     *
     * @param spinBitmask
     * @param reelIndex
     */
    isReelHeld(spinBitmask: number, reelIndex: number): boolean {
        return (spinBitmask & (1 << reelIndex)) === 0;
    }

    /**
     *
     * @param {number} reelIndexStart
     * @param {number} reelIndexStop
     * @param {number} symbolId
     * @returns {number}
     */
    protected getCountOfSymbolsOnSelectedReelsAndRows(
        reelIndexStart = 0,
        reelIndexStop: number,
        symbolId: number,
    ): number {
        let countSymbolsOnReelsAndRows = 0;

        for (let i = reelIndexStart; i <= reelIndexStop; i++) {
            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                if (this.stoppedSymbolsArray[i].symbols[j]) {
                    if (this.stoppedSymbolsArray[i].symbols[j] === symbolId) {
                        countSymbolsOnReelsAndRows++;
                    }
                }
            }
        }

        return countSymbolsOnReelsAndRows;
    }

    /**
     *
     * @returns {number}
     */
    public getTeasingSymbol(): number {
        return this.teasingSymbol;
    }

    public getTeasingSymbols(): Array<number> {
        return this.teasingSymbols;
    }

    //******************************************************************************************************************
    //  SET SYMBOLS TEASING
    //******************************************************************************************************************

    /**
     * Set teasing attributes (action,..).
     * @param {number} reelIndex
     * @returns {Array<ISymbolTeasingAttributes>}
     */
    public setSymbolsTeasingAttributes(reelIndex: number): Array<Array<ISymbolTeasingAttributes>> {
        if (this.teasingEnabled) {
            let countOfSymbol = 0;

            // first - get count of bonus symbols on stopped reels [0..reelIndex] (held reels excluded)
            for (let i = 0; i <= reelIndex; i++) {
                if (!this.isReelHeld(this.spinBitmask, i)) {
                    countOfSymbol += this.getCountOfSymbolsOnSelectedReelsAndRows(i, i, Settings.SymbolBonus);
                }
            }

            // second - get count of bonus symbols on held reels [0..reelCount]]
            for (let i = 0; i < this.reelCount; i++) {
                if (i != reelIndex) {
                    if (this.isReelHeld(this.spinBitmask, i)) {
                        countOfSymbol += this.getCountOfSymbolsOnSelectedReelsAndRows(i, i, Settings.SymbolBonus);
                    }
                }
            }

            // set attributes of symbols on reels
            for (let i = 0; i < this.reelCount; i++) {
                for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                    if (this.stoppedSymbolsArray[i].symbols[j] != null) {
                        if (this.stoppedSymbolsArray[i].symbols[j] === Settings.SymbolBonus) {
                            if (!this.isReelHeld(this.spinBitmask, i)) {
                                if (countOfSymbol === 1) {
                                    this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.IMPACT;
                                } else if (countOfSymbol > 1) {
                                    this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.TEASE;
                                }
                            }
                        } else {
                            if (countOfSymbol > 1) {
                                this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NOWIN;
                                //this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NORMAL;
                            } else {
                                this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NORMAL;
                            }
                        }
                    }
                }
            }
        } else {
            for (let i = 0; i <= reelIndex; i++) {
                for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                    this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NORMAL;
                }
            }
        }

        return this.symbolsTeasingAttributes;
    }

    /**
     * Set after re/spinReceived on held reels
     * @param reelIndex
     */
    public setSymbolsTeasingAttributesPrePhase(reelIndex: number): Array<Array<ISymbolTeasingAttributes>> {
        if (this.teasingEnabled) {
            let countOfSymbol = 0;

            // first - get count of teasing symbols on held reels
            for (let i = 0; i < this.reelCount; i++) {
                if (this.isReelHeld(this.spinBitmask, i)) {
                    countOfSymbol += this.getCountOfSymbolsOnSelectedReelsAndRows(i, i, Settings.SymbolBonus);
                }
            }

            for (let j = 0; j < this.stoppedSymbolsArray[reelIndex].symbols.length; j++) {
                if (this.stoppedSymbolsArray[reelIndex].symbols[j] != null) {
                    if (countOfSymbol >= this.minimumSymbolsForTeasing) {
                        if (this.stoppedSymbolsArray[reelIndex].symbols[j] === Settings.SymbolBonus) {
                            this.symbolsTeasingAttributes[reelIndex][j].action = SymbolTeasingActions.NORMAL;
                        } else {
                            this.symbolsTeasingAttributes[reelIndex][j].action = SymbolTeasingActions.NOWIN;
                            //this.symbolsTeasingAttributes[reelIndex][j].action = SymbolTeasingActions.NORMAL;
                        }
                    } else {
                        this.symbolsTeasingAttributes[reelIndex][j].action = SymbolTeasingActions.NORMAL;
                    }
                }
            }
        } else {
            for (let i = 0; i <= reelIndex; i++) {
                if (this.stoppedSymbolsArray != null && this.stoppedSymbolsArray[i] != null) {
                    for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                        this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NORMAL;
                    }
                }
            }
        }

        return this.symbolsTeasingAttributes;
    }

    /**
     *
     * @param {number} reelIndexStart
     * @param {number} reelIndexStop
     * @param {number} id
     * @param {number} action
     */
    protected symbolSetAction(reelIndexStart: number, reelIndexStop: number, id: number, action: number): void {
        for (let i = reelIndexStart; i <= reelIndexStop; i++) {
            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                if (this.stoppedSymbolsArray[i].symbols[j] != null) {
                    if (this.stoppedSymbolsArray[i].symbols[j] === id) {
                        this.symbolsTeasingAttributes[i][j].action = action;
                    } else {
                        this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NOWIN;
                    }
                }
            }
        }
    }

    //******************************************************************************************************************
    //  REELS TEASING ANIMATIONS
    //******************************************************************************************************************

    /**
     * Start animating teasing layers on reel stopped.
     *
     * @param reelIndex Reel stopped.
     */
    public animateTeasingOnReelStopped(reelIndex: number): void {
        let countSymbolsOnReelsAndRows = this.getCountOfSymbolsOnSelectedReelsAndRows(0, reelIndex, this.teasingSymbol);
        let countSymbolsOnCurrentReel = this.getCountOfSymbolsOnSelectedReelsAndRows(
            reelIndex,
            reelIndex,
            this.teasingSymbol,
        );

        this.countSymbolsOnReelsAndRows = countSymbolsOnReelsAndRows;

        if (this.teasingEnabled) {
            for (let i = reelIndex; i < this.reelCount; i++) {
                let reelIndexNext = i + 1;
                if (!this.isReelHeld(this.spinBitmask, reelIndexNext)) {
                    if (this.reelsTeasingAttributes[reelIndexNext] != null) {
                        if (this.reelsTeasingAttributes[reelIndexNext].action === ReelTeasingActions.TEASE) {
                            this.eventEmitter.emit(this.eventTeasingReel, reelIndexNext);

                            if (!this.wasPlayedTeaser) {
                                this.wasPlayedTeaser = true;
                                this.eventEmitter.emit(this.eventTeasing);
                            }
                        } else if (this.reelsTeasingAttributes[reelIndexNext].action === ReelTeasingActions.SPIN_STOP) {
                            this.removeTeasing(false);
                        }
                    } else {
                        this.removeTeasing(false);
                    }
                    break;
                }
            }

            if (countSymbolsOnCurrentReel) {
                if (countSymbolsOnReelsAndRows > 0 && countSymbolsOnReelsAndRows < 2) {
                    //PlayList.play('SND_SymScatterStop01');
                } else if (countSymbolsOnReelsAndRows >= 2) {
                    //PlayList.play('SND_SymScatterStop03');
                }
            }
        } else {
            //this.removeTeasing(false);
        }
    }

    /**
     *
     * @param reelIndex
     */
    public animateTeasingFirstSpinnedReel(reelIndex: number): void {
        if (this.teasingEnabled) {
            if (this.spinBitmask < 31) {
                // todo !!!
                if (this.reelsTeasingAttributes[reelIndex] != null) {
                    if (this.reelsTeasingAttributes[reelIndex].action === ReelTeasingActions.TEASE) {
                        if (this.elementInstance["teasingReel"] != null) {
                            this.eventEmitter.emit(this.eventTeasingReel, reelIndex);

                            this.wasPlayedTeaser = true;
                            this.eventEmitter.emit(this.eventTeasing);
                        }
                    }
                }
            }
        }
    }

    /**
     * Start animating teasing layers.
     *
     * @param reelIndex
     */
    public reelAnimationStart(reelIndex: number): void {}

    /**
     * Ex reelAnimationStop().
     */
    public animationStop(): void {
        this.countSymbolsOnReelsAndRows = this.getCountOfSymbolsOnSelectedReelsAndRows(
            0,
            this.reelCount - 1,
            this.teasingSymbol,
        );

        if (!this.teasingEnabled) {
            // In case of forceStop
            if (this.countSymbolsOnReelsAndRows > this.minimumSymbolsForTeasing) {
                if (!this.wasPlayedTeaserWin) {
                    this.wasPlayedTeaserWin = true;
                    this.eventEmitter.emit(this.eventTeasingWin);
                }
            }
        }
        this.finishTeasing();
    }

    /**
     * Remove teasing animations.
     * In case of enought teasing symbols count win sound is played else fail sound is played.
     *
     * @param {boolean} noWin
     */
    public removeTeasing(noWin: boolean = false): void {
        if (this.wasPlayedTeaser) {
            if (this.countSymbolsOnReelsAndRows > this.minimumSymbolsForTeasing) {
                if (!this.wasPlayedTeaserWin) {
                    this.wasPlayedTeaserWin = true;
                    this.eventEmitter.emit(this.eventTeasingWin);
                }
            } else {
                if (!this.wasPlayedTeaserFail) {
                    this.wasPlayedTeaserFail = true;
                    this.eventEmitter.emit(this.eventTeasingFail);
                }
            }
        }
    }

    public finishTeasing(): void {
        this.removeTeasing();

        this.countSymbolsOnReelsAndRows = 0;
        this.canPlayTeaser = false;
        this.wasPlayedTeaser = false;
        this.wasPlayedTeaserWin = false;
        this.wasPlayedTeaserFail = false;
    }

    public firstOccurrenceOfSymbolOnReel(reelIndex: number, symbolId: number): number {
        if (this.stoppedSymbolsArray[reelIndex].symbols.indexOf(symbolId) !== -1) {
            return reelIndex;
        }

        return -1;
    }

    //******************************************************************************************************************
    //  LOG
    //******************************************************************************************************************

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/TeasingBonus.ts
 **/